import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ApiClient, type ForecastPutBody } from '../api'
import type { EventListItem, ForecastPayload, ForecastPhase, ForecastContractBody } from '../types'

// useForecast — the data layer for one Idea's Forecast Studio workspace.
// Wraps the /events/{id}/forecast family. fc === null means "no forecast row
// yet" (404) → the UI offers "start forecast". Derived figures are computed in
// the tabs, never stored.

export function useIdeas() {
  return useQuery({
    queryKey: ['events'],
    queryFn: ApiClient.events,
    select: (rows: EventListItem[]) => rows.filter((e) => e.status === 'Idea')
  })
}

export function useForecast(eventId: number) {
  const qc = useQueryClient()
  const enabled = Number.isFinite(eventId) && eventId > 0

  const ideasQ = useIdeas()
  const eventQ = useQuery({ queryKey: ['events', eventId], queryFn: () => ApiClient.event(eventId), enabled })

  const fcQ = useQuery({
    queryKey: ['forecast', eventId],
    enabled,
    retry: false,
    queryFn: async (): Promise<ForecastPayload | null> => {
      try { return await ApiClient.forecast(eventId) } catch (err) {
        if ((err as Error).message.startsWith('404')) return null
        throw err
      }
    }
  })

  const setFc = (p: ForecastPayload) => qc.setQueryData(['forecast', eventId], p)

  const saveMut = useMutation({
    mutationFn: (body: Partial<ForecastPutBody>) => ApiClient.putForecast(eventId, body),
    onSuccess: setFc
  })
  const commitMut = useMutation({
    mutationFn: (opts?: { publishOnly?: boolean }) => ApiClient.commitForecast(eventId, opts),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['forecast', eventId] }); qc.invalidateQueries({ queryKey: ['events'] }) }
  })
  const addMemberMut = useMutation({
    mutationFn: (body: { personId: number; functionId: number; commitmentText?: string | null }) => ApiClient.addForecastMember(eventId, body),
    onSuccess: (res) => {
      const cur = qc.getQueryData<ForecastPayload | null>(['forecast', eventId])
      if (cur) setFc({ ...cur, team: res.team, phases: res.phases })
    }
  })
  const patchMemberMut = useMutation({
    mutationFn: ({ rowId, body }: { rowId: number; body: { commitmentText?: string | null; approvalStatus?: 'pending' | 'approved' | 'blocked' } }) => ApiClient.patchForecastMember(rowId, body),
    onSuccess: (res) => {
      const cur = qc.getQueryData<ForecastPayload | null>(['forecast', eventId])
      if (cur) setFc({ ...cur, team: res.team })
    }
  })
  const removeMemberMut = useMutation({
    mutationFn: (rowId: number) => ApiClient.removeForecastMember(rowId),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['forecast', eventId] })
  })
  const phasesMut = useMutation({
    mutationFn: (phases: Array<Omit<ForecastPhase, 'id'>>) => ApiClient.putForecastPhases(eventId, phases),
    onSuccess: (res) => {
      const cur = qc.getQueryData<ForecastPayload | null>(['forecast', eventId])
      if (cur) setFc({ ...cur, phases: res.phases })
    }
  })
  const invalidateForecast = () => qc.invalidateQueries({ queryKey: ['forecast', eventId] })
  const addContractMut = useMutation({ mutationFn: (body: ForecastContractBody) => ApiClient.createForecastContract(eventId, body), onSuccess: invalidateForecast })
  const updateContractMut = useMutation({ mutationFn: ({ cid, body }: { cid: number; body: ForecastContractBody }) => ApiClient.updateForecastContract(cid, body), onSuccess: invalidateForecast })
  const deleteContractMut = useMutation({ mutationFn: (cid: number) => ApiClient.deleteForecastContract(cid), onSuccess: invalidateForecast })
  const uploadContractDocMut = useMutation({ mutationFn: ({ cid, filename, dataBase64 }: { cid: number; filename: string; dataBase64: string }) => ApiClient.uploadContractDoc(cid, filename, dataBase64), onSuccess: invalidateForecast })
  const analyseContractMut = useMutation({ mutationFn: (cid: number) => ApiClient.analyseContract(cid), onSuccess: invalidateForecast })

  return {
    ideas: ideasQ.data ?? [],
    ev: eventQ.data ?? null,
    fc: fcQ.data ?? null,
    notFound: fcQ.data === null && fcQ.isFetched,
    isLoading: fcQ.isLoading || eventQ.isLoading,
    saving: saveMut.isPending,
    save: (body: Partial<ForecastPutBody>) => saveMut.mutateAsync(body),
    startForecast: () => saveMut.mutateAsync({ scenario: { att: 0, yieldPence: 0, costAdj: 0 } }),
    commit: (opts?: { publishOnly?: boolean }) => commitMut.mutateAsync(opts),
    committing: commitMut.isPending,
    addMember: (body: { personId: number; functionId: number; commitmentText?: string | null }) => addMemberMut.mutateAsync(body),
    patchMember: (rowId: number, body: { commitmentText?: string | null; approvalStatus?: 'pending' | 'approved' | 'blocked' }) => patchMemberMut.mutateAsync({ rowId, body }),
    removeMember: (rowId: number) => removeMemberMut.mutateAsync(rowId),
    savePhases: (phases: Array<Omit<ForecastPhase, 'id'>>) => phasesMut.mutateAsync(phases),
    addContract: (body: ForecastContractBody) => addContractMut.mutateAsync(body),
    updateContract: (cid: number, body: ForecastContractBody) => updateContractMut.mutateAsync({ cid, body }),
    deleteContract: (cid: number) => deleteContractMut.mutateAsync(cid),
    uploadContractDoc: (cid: number, filename: string, dataBase64: string) => uploadContractDocMut.mutateAsync({ cid, filename, dataBase64 }),
    analyseContract: (cid: number) => analyseContractMut.mutateAsync(cid),
    refetchForecast: invalidateForecast
  }
}
